<template>
  <div class="withdrawal-page">
    <tableComponents
      ref="tableComponents"
      :pagination="{
        currentPage: paginationForm.page,
        pageSize: paginationForm.page_size,
        total,
      }"
      :searchData="payoutsSearchData"
      :isHaveAdd="false"
      :isHaveExport="false"
      :sizes="[15, 20, 50, 100]"
      @handleCurrentChange="handleCurrentChange"
      @handleSizeChange="handleSizeChange"
      @sendUserSearch="getSearch"
    >
      <template #table>
        <el-table v-if="!isMobileViewport" :data="list" border stripe v-loading="loading" style="width: 100%">
          <el-table-column prop="withdrawal_no" label="提现单号" min-width="180" />
          <el-table-column label="用户" min-width="170">
            <template slot-scope="{ row }">
              <div class="user-cell">
                <div class="user-cell__name">
                  {{ getUserName(row) }}
                </div>
                <div class="user-cell__sub">ID：{{ row.user_id || "--" }}</div>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="APP/渠道" min-width="170">
            <template slot-scope="{ row }">
              <div>{{ row.app && row.app.app_name ? row.app.app_name : row.app_id || "--" }}</div>
              <div class="muted">渠道：{{ row.channel_id || "--" }}</div>
            </template>
          </el-table-column>
          <el-table-column prop="payment_company_name" label="付款公司" min-width="180" />
          <el-table-column label="收款账户" min-width="220">
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.account_type === 'wechat' ? 'success' : ''">
                {{ row.account_type_label || getAccountTypeLabel(row) }}
              </el-tag>
              <div class="account-line">{{ getAccountPrimary(row) }}</div>
              <div class="muted">{{ getAccountSecondary(row) }}</div>
            </template>
          </el-table-column>
          <el-table-column label="提现金额" min-width="150" align="right">
            <template slot-scope="{ row }">
              <div class="amount">{{ formatAmount(row) }}</div>
              <div class="muted">{{ formatRawMoney(row.money) }}</div>
            </template>
          </el-table-column>
          <el-table-column label="状态" width="110" align="center">
            <template slot-scope="{ row }">
              <el-tag :type="getStatusTagType(row.status)">
                {{ row.status_label || getStatusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column label="申请时间" min-width="160" align="center">
            <template slot-scope="{ row }">
              {{ formatTime(row.created_time) }}
            </template>
          </el-table-column>
          <el-table-column label="处理时间" min-width="160" align="center">
            <template slot-scope="{ row }">
              {{ formatTime(row.pay_time || row.audit_time) }}
            </template>
          </el-table-column>
          <el-table-column
            label="操作"
            :width="isMobileViewport ? 76 : 250"
            align="center"
            fixed="right"
          >
            <template slot-scope="{ row }">
              <responsive-actions
                :items="getPayoutRowActions(row)"
                @select="handlePayoutRowAction($event, row)"
              />
            </template>
          </el-table-column>
        </el-table>
        <mobile-record-list
          v-else
          :rows="list"
          :loading="loading"
          :title="getMobilePayoutTitle"
          :subtitle="getMobilePayoutSubtitle"
          :fields="mobilePayoutFields"
          :actions="getPayoutRowActions"
          @action="handlePayoutRowAction"
        />
      </template>
    </tableComponents>

    <el-dialog
      title="提现详情"
      :visible.sync="detailVisible"
      width="760px"
      v-if="detailVisible"
    >
      <div v-loading="detailLoading">
        <el-descriptions :column="2" border>
          <el-descriptions-item label="提现单号">
            {{ payInfo.withdrawal_no || "--" }}
          </el-descriptions-item>
          <el-descriptions-item label="状态">
            <el-tag :type="getStatusTagType(payInfo.status)">
              {{ payInfo.status_label || getStatusLabel(payInfo.status) }}
            </el-tag>
          </el-descriptions-item>
          <el-descriptions-item label="用户">
            {{ getUserName(payInfo) }}（ID：{{ payInfo.user_id || "--" }}）
          </el-descriptions-item>
          <el-descriptions-item label="APP">
            {{ payInfo.app && payInfo.app.app_name ? payInfo.app.app_name : payInfo.app_id || "--" }}
          </el-descriptions-item>
          <el-descriptions-item label="渠道">
            {{ payInfo.channel_id || "--" }}
          </el-descriptions-item>
          <el-descriptions-item label="提现金额">
            {{ formatAmount(payInfo) }}（{{ formatRawMoney(payInfo.money) }}）
          </el-descriptions-item>
          <el-descriptions-item label="收款类型">
            {{ payInfo.account_type_label || getAccountTypeLabel(payInfo) }}
          </el-descriptions-item>
          <el-descriptions-item label="收款人" v-if="!isWechatOrder(payInfo)">
            {{ payInfo.account_name || "--" }}
          </el-descriptions-item>
          <el-descriptions-item label="收款账号" :span="2" v-if="!isWechatOrder(payInfo)">
            {{ payInfo.account_no || "--" }}
          </el-descriptions-item>
          <el-descriptions-item label="微信 OpenID" :span="2" v-if="isWechatOrder(payInfo)">
            {{ payInfo.account_openid || "--" }}
          </el-descriptions-item>
          <el-descriptions-item label="账单流水">
            {{ payInfo.bill_id || "--" }}
          </el-descriptions-item>
          <el-descriptions-item label="打款方式">
            {{ payInfo.pay_type === "api" ? "审核后接口转账" : "历史人工打款" }}
          </el-descriptions-item>
          <el-descriptions-item label="付款公司">{{ payInfo.payment_company_name || "待绑定" }}</el-descriptions-item>
          <el-descriptions-item label="付款商户">{{ payInfo.payment_merchant_id || "--" }}</el-descriptions-item>
          <el-descriptions-item label="平台状态">{{ payInfo.payment_remote_status || "--" }}</el-descriptions-item>
          <el-descriptions-item label="转账单号">{{ payInfo.payment_out_no || "--" }}</el-descriptions-item>
          <el-descriptions-item v-if="payInfo.payment_error" label="转账提示" :span="2">{{ payInfo.payment_error }}</el-descriptions-item>
          <el-descriptions-item label="申请时间">
            {{ formatTime(payInfo.created_time) }}
          </el-descriptions-item>
          <el-descriptions-item label="处理时间">
            {{ formatTime(payInfo.pay_time || payInfo.audit_time) }}
          </el-descriptions-item>
          <el-descriptions-item label="拒绝原因" :span="2" v-if="payInfo.reject_reason">
            {{ payInfo.reject_reason }}
          </el-descriptions-item>
          <el-descriptions-item label="备注" :span="2">
            {{ payInfo.remark || "--" }}
          </el-descriptions-item>
        </el-descriptions>

        <div class="audit-log-title" v-if="auditLogs.length">操作日志</div>
        <el-table
          v-if="auditLogs.length"
          :data="auditLogs"
          border
          size="mini"
          class="audit-log-table"
        >
          <el-table-column type="expand" width="42">
            <template slot-scope="{ row }">
              <div v-if="paymentLog(row).operation" class="payment-log-detail">
                <div><strong>平台错误码：</strong>{{ paymentLog(row).code || '--' }} / {{ paymentLog(row).sub_code || '--' }}</div>
                <div><strong>平台说明：</strong>{{ paymentLog(row).sub_msg || paymentLog(row).msg || paymentLog(row).fail_reason || '--' }}</div>
                <div><strong>请求追踪号：</strong>{{ paymentLog(row).trace_id || '--' }}</div>
                <div><strong>响应验签：</strong>{{ signatureLabel(paymentLog(row).signature_verified) }}</div>
                <div><strong>异常类型：</strong>{{ paymentLog(row).exception_name || '--' }} {{ paymentLog(row).exception_code || '' }}</div>
                <div><strong>异常说明：</strong>{{ paymentLog(row).exception_message || '--' }}</div>
                <div><strong>HTTP 状态：</strong>{{ paymentLog(row).http_status || '--' }}</div>
                <div><strong>本次结果：</strong>{{ paymentLog(row).result_state || '--' }} · {{ paymentLog(row).message || '--' }}</div>
              </div>
              <div v-else class="payment-log-detail">{{ row.remark || '此条操作没有平台返回信息' }}</div>
            </template>
          </el-table-column>
          <el-table-column label="动作" width="145"><template slot-scope="{ row }">{{ paymentActionLabel(row.action) }}</template></el-table-column>
          <el-table-column prop="from_status" label="原状态" width="120" />
          <el-table-column prop="to_status" label="新状态" width="120" />
          <el-table-column prop="remark" label="备注" min-width="180" />
          <el-table-column label="时间" width="160">
            <template slot-scope="{ row }">
              {{ formatTime(row.created_time) }}
            </template>
          </el-table-column>
        </el-table>
      </div>
      <span slot="footer" class="dialog-footer">
        <el-button @click="detailVisible = false">关闭</el-button>
        <el-button v-for="action in getPayoutRowActions(payInfo).filter(item => item.key !== 'detail')" :key="action.key"
          :type="action.type" :loading="actionLoading" @click="handlePayoutRowAction(action.key, payInfo)">{{ action.label }}</el-button>
      </span>
    </el-dialog>

    <el-dialog
      title="拒绝提现"
      :visible.sync="rejectVisible"
      width="460px"
      @closed="resetRejectDialog"
    >
      <el-form :model="rejectForm" label-width="90px">
        <el-form-item label="提现单号">
          <el-input :value="currentOrder.withdrawal_no || currentOrder.id || ''" disabled />
        </el-form-item>
        <el-form-item label="拒绝原因">
          <el-input
            v-model.trim="rejectForm.reject_reason"
            type="textarea"
            :autosize="{ minRows: 3, maxRows: 5 }"
            placeholder="请输入拒绝原因"
          />
        </el-form-item>
      </el-form>
      <span slot="footer" class="dialog-footer">
        <el-button @click="rejectVisible = false">取消</el-button>
        <el-button type="danger" :loading="actionLoading" @click="submitReject">
          确认拒绝
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import { payoutsSearchData } from "@/assets/js/searchData";
import responsiveMixin from "@/mixins/responsive";

export default {
  mixins: [responsiveMixin],
  data() {
    return {
      payoutsSearchData,
      loading: false,
      detailLoading: false,
      actionLoading: false,
      detailVisible: false,
      rejectVisible: false,
      list: [],
      queryForm: {},
      total: 0,
      paginationForm: {
        page: 1,
        page_size: 15,
      },
      payInfo: {},
      currentOrder: {},
      rejectForm: {
        reject_reason: "",
      },
    };
  },
  computed: {
    mobilePayoutFields() {
      return [
        { label: "用户", value: (row) => this.getUserName(row) },
        { label: "提现金额", value: (row) => this.formatAmount(row) },
        {
          label: "状态",
          value: (row) => row.status_label || this.getStatusLabel(row.status),
          tag: true,
          tagType: (row) => this.getStatusTagType(row.status),
        },
        { label: "申请时间", value: (row) => this.formatTime(row.created_time) },
        { label: "提现单号", key: "withdrawal_no", summary: false },
        { label: "用户ID", key: "user_id", summary: false },
        {
          label: "APP",
          value: (row) => (row.app && row.app.app_name) || row.app_id,
          summary: false,
        },
        { label: "渠道", key: "channel_id", summary: false },
        { label: "付款公司", key: "payment_company_name", summary: false },
        {
          label: "收款类型",
          value: (row) => row.account_type_label || this.getAccountTypeLabel(row),
          summary: false,
        },
        { label: "收款人", value: (row) => this.getAccountPrimary(row), summary: false },
        { label: "收款账号", value: (row) => this.getAccountSecondary(row), summary: false },
        {
          label: "处理时间",
          value: (row) => this.formatTime(row.pay_time || row.audit_time),
          summary: false,
        },
      ];
    },
    auditLogs() {
      return Array.isArray(this.payInfo.audit_logs) ? this.payInfo.audit_logs : [];
    },
  },
  methods: {
    paymentLog(row) {
      if (!row || !row.ext_json) return {};
      if (typeof row.ext_json === 'object') return row.ext_json;
      try { return JSON.parse(row.ext_json) || {}; } catch (_) { return {}; }
    },
    signatureLabel(value) { return value === true ? '已通过' : value === false ? '未通过，仅供排查' : '无可验证响应'; },
    paymentActionLabel(action) {
      return { payment_submit_result: '转账接口返回', payment_query_result: '查单接口返回', payment_settle_error: '账单结算异常',
        approve_transfer: '审核并转账', transfer_paid: '确认到账', transfer_failed: '确认失败', apply: '提交申请' }[action] || action;
    },
    async paymentAction(action, row) {
      const prompts = {
        approve: `将使用「${row.payment_company_name}」的商户 ${row.payment_merchant_id} 向 ${this.getConfirmTarget(row)} 转账 ${this.formatAmount(row)}，确认审核通过？`,
        prepare_legacy: "将此历史订单绑定到 APP 当前付款公司。请先确认未在线下付款，绑定后只能走接口转账。",
        resubmit: "先查询原订单，仅在平台明确返回未找到时，使用原商户和原单号补发。确认继续？",
      };
      if (this.actionLoading) return;
      if (prompts[action]) {
        try { await this.$confirm(prompts[action], "提现处理", { type: "warning" }); } catch (_) { return; }
      }
      this.actionLoading = true;
      try {
        const response = await this.$api.getPayouts.paymentAction(action, row.id);
        if (response && response.code === 200) {
          if (response.data && response.data.payment_error) this.$message.warning(response.data.payment_error);
          else this.$message.success(response.msg);
          if (this.detailVisible) this.payInfo = response.data;
          await this.getPayoutsList();
        }
      } finally { this.actionLoading = false; }
    },
    getMobilePayoutTitle(row) {
      return row.withdrawal_no || `提现 #${row.id}`;
    },
    getMobilePayoutSubtitle(row) {
      return `提现ID ${row.id}`;
    },
    getPayoutRowActions(row) {
      const actions = [{ key: "detail", label: "详情", type: "primary" }];
      if (row.status === "pending") {
        if (row.pay_type === "api") actions.push({ key: "approve", label: "审核并转账", type: "success" });
        else if (this.$store.getters.isSuperAdmin) {
          actions.push({ key: "prepare_legacy", label: "绑定付款配置", type: "primary" });
          actions.push({ key: "confirm", label: "确认线下已打款", type: "success" });
        }
        actions.push({ key: "reject", label: "拒绝", type: "danger" });
      }
      if (row.status === "processing") {
        actions.push({ key: "query", label: "查询到账", type: "primary" });
        actions.push({ key: "resubmit", label: "原单补发", type: "warning" });
      }
      return actions;
    },
    handlePayoutRowAction(command, row) {
      if (["approve", "query", "resubmit", "prepare_legacy"].includes(command)) return this.paymentAction(command, row);
      const handlers = {
        detail: () => this.openDetail(row.id),
        confirm: () => this.confirmPaid(row),
        reject: () => this.openReject(row),
      };
      if (handlers[command]) {
        handlers[command]();
      }
    },
    getUserName(row) {
      if (row.user) {
        return row.user.nickname || row.user.username || "--";
      }
      return row.nickname || row.username || "--";
    },
    getAccountTypeLabel(row) {
      if (row.account_type === "wechat" || String(row.payouts_type) === "1") {
        return "微信";
      }
      return "支付宝";
    },
    isWechatOrder(row) {
      return row.account_type === "wechat" || String(row.payouts_type) === "1";
    },
    getAccountPrimary(row) {
      if (this.isWechatOrder(row)) {
        return row.account_openid ? "微信 OpenID" : "--";
      }
      return row.account_name || "--";
    },
    getAccountSecondary(row) {
      if (this.isWechatOrder(row)) {
        return row.account_openid || "--";
      }
      return row.account_no || "--";
    },
    getConfirmTarget(row) {
      if (this.isWechatOrder(row)) {
        return row.account_openid ? `微信 OpenID：${row.account_openid}` : "该微信用户";
      }
      return row.account_name || "该用户";
    },
    getStatusLabel(status) {
      const labelMap = {
        pending: "待审核",
        processing: "转账处理中",
        failed: "打款失败，余额已退回",
        paid: "已打款",
        rejected: "已拒绝",
      };
      return labelMap[status] || "--";
    },
    getStatusTagType(status) {
      const typeMap = {
        pending: "warning",
        processing: "warning",
        failed: "danger",
        paid: "success",
        rejected: "danger",
      };
      return typeMap[status] || "info";
    },
    formatTime(value) {
      return value ? this.$fnc.getTimeFormat(value, "ymdhms") : "--";
    },
    formatRawMoney(value) {
      const amount = Number(value || 0);
      return `${amount.toFixed(0)} 项目单位`;
    },
    formatAmount(row) {
      if (row.amount_yuan !== undefined && row.amount_yuan !== null && row.amount_yuan !== "") {
        return `${Number(row.amount_yuan).toFixed(2)} 元`;
      }
      return `${(Number(row.money || 0) / 100000).toFixed(2)} 元`;
    },
    async getPayoutsList() {
      this.loading = true;
      try {
        const data = await this.$api.getPayouts.getPayoutsList({
          ...this.paginationForm,
          ...this.queryForm,
        });
        const payload = data.data || {};
        this.total = payload.count || 0;
        this.list = Array.isArray(payload.list) ? payload.list : [];
      } finally {
        this.loading = false;
      }
    },
    async openDetail(id) {
      this.detailVisible = true;
      this.detailLoading = true;
      try {
        const data = await this.$api.getPayouts.getPayoutsInfo({ id });
        this.payInfo = data.data || {};
      } finally {
        this.detailLoading = false;
      }
    },
    confirmPaid(row) {
      this.$confirm(
        `确认已经线下打款给 ${this.getConfirmTarget(row)} 吗？`,
        "确认已打款",
        {
          confirmButtonText: "确认已打款",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(async () => {
          this.actionLoading = true;
          try {
            const data = await this.$api.getPayouts.confirmPaid({
              id: row.id,
              remark: "后台人工确认已打款",
            });
            if (data.code === 200) {
              this.$message.success(data.msg || "已确认打款");
              this.detailVisible = false;
              await this.getPayoutsList();
            }
          } finally {
            this.actionLoading = false;
          }
        })
        .catch(() => {});
    },
    openReject(row) {
      this.currentOrder = row || {};
      this.rejectVisible = true;
    },
    resetRejectDialog() {
      this.currentOrder = {};
      this.rejectForm = {
        reject_reason: "",
      };
    },
    async submitReject() {
      const reason = this.rejectForm.reject_reason.trim();
      if (!reason) {
        this.$message.warning("请输入拒绝原因");
        return;
      }

      this.actionLoading = true;
      try {
        const data = await this.$api.getPayouts.reject({
          id: this.currentOrder.id,
          reject_reason: reason,
          remark: reason,
        });
        if (data.code === 200) {
          this.$message.success(data.msg || "已拒绝提现");
          this.rejectVisible = false;
          this.detailVisible = false;
          await this.getPayoutsList();
        }
      } finally {
        this.actionLoading = false;
      }
    },
    handleCurrentChange(val) {
      this.paginationForm.page = val;
      this.getPayoutsList();
    },
    handleSizeChange(val) {
      this.paginationForm.page_size = val;
      this.paginationForm.page = 1;
      this.getPayoutsList();
    },
    getSearch(form) {
      this.queryForm = form || {};
      this.paginationForm.page = 1;
      this.getPayoutsList();
    },
  },
  created() {
    this.getPayoutsList();
  },
};
</script>

<style lang="less" scoped>
.withdrawal-page {
  width: 100%;
}

.user-cell__name {
  font-weight: 600;
  color: #203864;
}

.user-cell__sub,
.muted {
  margin-top: 4px;
  color: #7a8da9;
  font-size: 12px;
}

.account-line {
  margin-top: 6px;
  color: #303133;
}

.amount {
  font-weight: 600;
  color: #303133;
}

.audit-log-title {
  margin: 18px 0 10px;
  font-weight: 600;
  color: #203864;
}

.payment-log-detail { padding: 8px 18px; line-height: 1.8; white-space: normal; overflow-wrap: anywhere; }

.audit-log-table {
  margin-top: 8px;
}

/deep/ .el-table .el-table__cell {
  padding: 7px 0;
}
</style>
